/**
 * photoUpload.ts — client-side photo upload helpers.
 *
 * Three-step upload flow (Pattern 8):
 *   1. requestUploadUrl: POST /photos/upload-url → { uploadUrl, photoId, key, expiresAt }
 *   2. uploadPhotoToS3: fetch PUT to S3 presigned URL (browser direct, no Lambda middleman)
 *   3. pollPhotoReady: poll GET /photos until target photoId.status === 'ready' (Lambda processed)
 *
 * Security: Authorization header uses the Cognito id_token (not access token).
 * CORS note (Pitfall 13): the browser PUT to S3 sends Content-Type — the bucket CORS
 * policy allows PUT from the app origins.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const REST_API_URL = import.meta.env.VITE_REST_API_URL ?? '';

async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No id_token — user is not authenticated');
  return token;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UploadUrlResponse {
  uploadUrl: string;
  photoId: string;
  key: string;
  expiresAt: number;
}

export interface PhotoListItem {
  photoId: string;
  s3Key: string;
  uploadedAt: string;
  status: 'pending' | 'ready' | 'error';
  contentType: string;
  viewUrl: string | null;
}

// ── requestUploadUrl ──────────────────────────────────────────────────────────
/**
 * Requests a presigned S3 PUT URL from the REST API.
 * Returns { uploadUrl, photoId, key, expiresAt }.
 */
export async function requestUploadUrl(
  filename: string,
  contentType: string,
): Promise<UploadUrlResponse> {
  const token = await getIdToken();

  const response = await fetch(`${REST_API_URL}/photos/upload-url`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ filename, contentType }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to get upload URL (${response.status}): ${body}`);
  }

  return response.json() as Promise<UploadUrlResponse>;
}

// ── uploadPhotoToS3 ───────────────────────────────────────────────────────────
/**
 * Uploads a File directly to S3 via the presigned PUT URL.
 * Browser PUT — file bytes go directly to S3, not through Lambda.
 *
 * Note: Only Content-Type header is sent. The sub/photoId metadata is
 * bound into the presigned URL signature at server time (not sent as headers
 * from the browser — presigned metadata is verified by server signature).
 */
export async function uploadPhotoToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed (${response.status}): ${await response.text()}`);
  }
}

// ── pollPhotoReady ────────────────────────────────────────────────────────────
/**
 * Polls GET /photos until the target photoId has status === 'ready'.
 * Returns the photo item when ready, or throws on timeout.
 *
 * Default: poll every 1.5 seconds, timeout after 15 seconds.
 * The photo-processor Lambda typically completes within 1–3 seconds.
 */
export async function pollPhotoReady(
  photoId: string,
  intervalMs = 1500,
  timeoutMs = 15000,
): Promise<PhotoListItem> {
  const token = await getIdToken();
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const response = await fetch(`${REST_API_URL}/photos`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error(`Failed to poll photos (${response.status})`);
    }

    const data = await response.json() as { photos: PhotoListItem[] };
    const target = data.photos.find((p) => p.photoId === photoId);

    if (target?.status === 'ready') {
      return target;
    }

    if (target?.status === 'error') {
      throw new Error('Photo processing failed on the server. Please try again.');
    }

    // Not ready yet — wait before next poll
    await new Promise<void>((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('Photo processing timed out. The server may be busy — please try again.');
}
